import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Chip,
  MenuItem,
  TextField,
  Button,
  Alert,
  Divider,
} from "@mui/material";
import Header from "../../components/Header";
import { useGetConflictsQuery, useResolveConflictMutation, useGetReviewQuery } from "../../services/review.service";

const VALUE_OPTIONS = [
  { value: "include", label: "Include" },
  { value: "exclude", label: "Exclude" },
  { value: "not_retrieved", label: "Full text not retrieved" },
];

const ResolveForm = ({ reviewId, recordId, exclusionReasons, existing }) => {
  const [value, setValue] = useState(existing?.value || "");
  const [reason, setReason] = useState(existing?.reason || "");
  const [resolveConflict, { isLoading }] = useResolveConflictMutation();
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!value) return;
    if (value === "exclude" && !reason) return;
    await resolveConflict({ reviewId, recordId, value, reason: value === "exclude" ? reason : undefined });
    setDone(true);
  };

  return (
    <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mt={1}>
      <TextField select size="small" label="Final decision" value={value} onChange={(e) => setValue(e.target.value)} sx={{ minWidth: 200 }}>
        {VALUE_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </TextField>
      {value === "exclude" && (
        <TextField select size="small" label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ minWidth: 200 }}>
          {exclusionReasons.map((r) => (
            <MenuItem key={r} value={r}>{r}</MenuItem>
          ))}
        </TextField>
      )}
      <Button variant="contained" size="small" disabled={isLoading || !value} onClick={submit}>
        {existing ? "Update resolution" : "Resolve"}
      </Button>
      {done && <Chip label="Saved" color="success" size="small" />}
    </Box>
  );
};

const Stage3Conflicts = ({ reviewId, exclusionReasons }) => {
  const { data, isLoading, error } = useGetConflictsQuery({ reviewId, stage: 3 });

  if (error) {
    return <Alert severity="warning">{error.data?.message || "Stage 3 is still blinded, or you don't have access."}</Alert>;
  }
  if (isLoading) return <Typography>Loading...</Typography>;

  const conflicts = data?.conflicts || [];
  if (conflicts.length === 0) return <Alert severity="success">No Stage 3 conflicts right now.</Alert>;

  return (
    <List>
      {conflicts.map((c) => (
        <Paper key={c.record._id} sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1">{c.record.title}</Typography>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {c.kind === "reason" ? "Everyone excluded, but the reasons differ" : "Reviewers disagree"}
          </Typography>
          <List dense>
            {c.decisions.map((d, i) => (
              <ListItem key={i} disableGutters>
                <ListItemText primary={`${d.value}${d.reason ? ` — ${d.reason}` : ""}`} />
              </ListItem>
            ))}
          </List>
          {c.resolution && (
            <Chip
              sx={{ mb: 1 }}
              label={`Resolved: ${c.resolution.value}${c.resolution.reason ? ` (${c.resolution.reason})` : ""} by ${c.resolution.resolvedBy?.username || "?"}`}
              color="info"
            />
          )}
          <ResolveForm reviewId={reviewId} recordId={c.record._id} exclusionReasons={exclusionReasons} existing={c.resolution} />
        </Paper>
      ))}
    </List>
  );
};

const Stage12Disagreements = ({ reviewId, stage }) => {
  const { data, isLoading, error } = useGetConflictsQuery({ reviewId, stage });

  if (error) {
    return <Alert severity="warning">{error.data?.message || `Stage ${stage} is still blinded, or you don't have access.`}</Alert>;
  }
  if (isLoading) return <Typography>Loading...</Typography>;

  const disagreements = data?.disagreements || [];
  if (disagreements.length === 0) return <Alert severity="success">No Stage {stage} disagreements right now.</Alert>;

  return (
    <List>
      {disagreements.map((d) => (
        <Paper key={d.record._id} sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1">{d.record.title}</Typography>
          <List dense>
            {d.decisions.map((dec, i) => (
              <ListItem key={i} disableGutters>
                <ListItemText primary={dec.value} />
              </ListItem>
            ))}
          </List>
        </Paper>
      ))}
    </List>
  );
};

const Conflicts = () => {
  const { reviewId } = useParams();
  const { data: reviewData } = useGetReviewQuery(reviewId);
  const [tab, setTab] = useState(0);
  const stages = [3, 2, 1];
  const stage = stages[tab];

  return (
    <Box m="1.5rem 2.5rem">
      <Header title="Conflicts" subtitle="Stage 3 disagreements to resolve, plus Stage 1-2 disagreements for information" />
      <Divider sx={{ my: 2 }} />
      <Tabs value={tab} onChange={(e, v) => setTab(v)}>
        <Tab label="Stage 3 (resolve)" />
        <Tab label="Stage 2 (info only)" />
        <Tab label="Stage 1 (info only)" />
      </Tabs>
      <Box mt={2}>
        {stage === 3 ? (
          <Stage3Conflicts reviewId={reviewId} exclusionReasons={reviewData?.review?.exclusionReasons || []} />
        ) : (
          <Stage12Disagreements reviewId={reviewId} stage={stage} />
        )}
      </Box>
    </Box>
  );
};

export default Conflicts;
